import { lazy } from 'react';
import type { ComponentType } from 'react';

const RELOAD_FLAG = 'p121_chunk_reload_at';

/**
 * Fallo típico de SPA tras un deploy: la pestaña abierta referencia chunks
 * hasheados que ya no existen en el servidor (Vercel solo sirve los assets
 * del deploy vigente), y el import() dinámico revienta al navegar a una
 * ruta lazy ("Failed to fetch dynamically imported module").
 */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|unable to preload css/i.test(
    message,
  );
}

/**
 * Recarga la página para tomar el index.html del deploy nuevo. Guard de 60s
 * en sessionStorage para no entrar en loop si el chunk sigue fallando tras
 * recargar (ej. problema de red y no un deploy).
 *
 * @returns true si disparó la recarga; false si ya se recargó hace poco (o
 * no hay sessionStorage para el guard) y el error debe propagarse.
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
    if (Date.now() - lastReload < 60_000) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * React.lazy() resistente a deploys: si el import dinámico falla porque el
 * chunk ya no existe, recarga la página una vez en lugar de romper la ruta.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isStaleChunkError(error) && reloadOnceForStaleChunk()) {
        // La página ya se está recargando; dejamos la promesa pendiente para
        // que Suspense siga mostrando el skeleton mientras tanto.
        return await new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
